import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Plus, Megaphone, Play, XCircle, CheckCircle2, Clock } from "lucide-react";
import CampaignBuilder from "./CampaignBuilder";
import CampaignRunner from "./CampaignRunner";

const STATUS_STYLES = {
  draft: { icon: Clock, color: "bg-zinc-100 text-zinc-700 border-zinc-200" },
  running: { icon: Play, color: "bg-amber-100 text-amber-800 border-amber-200" },
  completed: { icon: CheckCircle2, color: "bg-primary-100 text-primary-800 border-primary-200" },
  cancelled: { icon: XCircle, color: "bg-red-100 text-red-700 border-red-200" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch { return "—"; }
}

export default function CampaignsPanel() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [runnerId, setRunnerId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/campaigns");
      setCampaigns(data || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const cancel = async (c) => {
    if (!window.confirm(`Cancel campaign "${c.name}"?`)) return;
    try {
      await api.post(`/campaigns/${c.id}/cancel`);
      toast.success("Cancelled");
      load();
    } catch {
      toast.error("Could not cancel");
    }
  };

  return (
    <div data-testid="campaigns-panel" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-muted-foreground">Campaigns</p>
          <h2 className="font-display text-2xl font-black tracking-tight">Outreach runs</h2>
          <p className="text-xs text-muted-foreground mt-1">Targeted, manual-send WhatsApp outreach. Open one tab at a time — keeps you on the right side of WhatsApp's rules.</p>
        </div>
        <Button onClick={() => setBuilderOpen(true)} data-testid="new-campaign-btn">
          <Plus className="h-4 w-4 mr-1.5" /> New campaign
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground py-10 text-center">Loading campaigns…</p>
      ) : campaigns.length === 0 ? (
        <Card className="rounded-3xl border-dashed border-2 border-border/60">
          <CardContent className="py-12 text-center">
            <Megaphone className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
            <p className="font-semibold">No campaigns yet.</p>
            <p className="text-sm text-muted-foreground mt-1">Pick a segment, write a message, start reaching out — one at a time.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {campaigns.map((c) => {
            const style = STATUS_STYLES[c.status] || STATUS_STYLES.draft;
            const StatusIcon = style.icon;
            const pct = c.total_count ? Math.round(((c.sent_count + (c.skipped_count || 0) + (c.failed_count || 0)) / c.total_count) * 100) : 0;
            return (
              <Card key={c.id} className="rounded-3xl border-border/60 shadow-soft card-lift" data-testid={`campaign-${c.id}`}>
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-semibold truncate">{c.name}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {c.segment_name || "—"} · {fmtDate(c.created_at)}
                      </p>
                    </div>
                    <Badge variant="outline" className={`${style.color} text-[10px] uppercase tracking-wider shrink-0 inline-flex items-center gap-1`}>
                      <StatusIcon className="h-2.5 w-2.5" /> {c.status}
                    </Badge>
                  </div>

                  <div className="grid grid-cols-4 gap-2 text-center">
                    <div>
                      <p className="font-display text-xl font-black">{c.total_count || 0}</p>
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Total</p>
                    </div>
                    <div>
                      <p className="font-display text-xl font-black text-primary">{c.sent_count || 0}</p>
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Sent</p>
                    </div>
                    <div>
                      <p className="font-display text-xl font-black text-amber-600">{c.skipped_count || 0}</p>
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Skipped</p>
                    </div>
                    <div>
                      <p className="font-display text-xl font-black text-red-600">{c.failed_count || 0}</p>
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Failed</p>
                    </div>
                  </div>

                  <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                    <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
                  </div>

                  <div className="flex items-center gap-1.5 pt-2 border-t border-border/60">
                    {c.status === "running" && (
                      <Button size="sm" onClick={() => setRunnerId(c.id)} data-testid={`camp-run-${c.id}`}>
                        <Play className="h-3.5 w-3.5 mr-1" /> Continue sending
                      </Button>
                    )}
                    {c.status === "completed" && (
                      <Button size="sm" variant="ghost" onClick={() => setRunnerId(c.id)} data-testid={`camp-review-${c.id}`}>
                        Review
                      </Button>
                    )}
                    {(c.status === "running" || c.status === "draft") && (
                      <Button size="sm" variant="ghost" onClick={() => cancel(c)} className="text-red-600 hover:text-red-700 ml-auto" data-testid={`camp-cancel-${c.id}`}>
                        Cancel
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <CampaignBuilder
        open={builderOpen}
        onClose={() => setBuilderOpen(false)}
        onCreated={(c) => { load(); setRunnerId(c.id); }}
      />
      <CampaignRunner
        campaignId={runnerId}
        open={!!runnerId}
        onClose={() => { setRunnerId(null); load(); }}
        onProgress={() => { /* fresh on close */ }}
      />
    </div>
  );
}
